/**
 * Quarter anchoring for the EOS suite — the quarter-grained sibling of
 * `lib/scorecards/weeks.ts`. Frontend mirror of `server/src/lib/eos/quarters.ts`;
 * the two impls MUST stay in lockstep, and they agree because
 * both pin to UTC midnight.
 *
 * Convention (ADR-023): `quarterStart` is the first day of the calendar
 * quarter at UTC midnight — 2026-07-01T00:00:00.000Z for Q3 2026. Every
 * EOS write that carries a quarter validates through `parseQuarterStart`.
 */

/** First day (UTC midnight) of the calendar quarter containing `date`. */
export function quarterStartOf(date: Date): Date {
  const month = date.getUTCMonth();
  const quarterMonth = month - (month % 3);
  return new Date(Date.UTC(date.getUTCFullYear(), quarterMonth, 1));
}

/**
 * Parse + validate a `YYYY-MM-DD` quarter-start string. Returns the Date
 * only when it is EXACTLY a quarter boundary (Jan/Apr/Jul/Oct 1st);
 * throws otherwise so route handlers surface a 400, mirroring the
 * scorecards' Monday validation.
 */
export function parseQuarterStart(raw: string): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    throw new Error(`quarterStart must be YYYY-MM-DD (got ${JSON.stringify(raw)})`);
  }
  const date = new Date(`${raw}T00:00:00.000Z`);
  if (Number.isNaN(date.valueOf())) {
    throw new Error(`quarterStart is not a valid date: ${JSON.stringify(raw)}`);
  }
  if (date.getUTCDate() !== 1 || date.getUTCMonth() % 3 !== 0) {
    throw new Error(
      `quarterStart must be the first day of a calendar quarter (Jan/Apr/Jul/Oct 1st), got ${raw}`,
    );
  }
  return date;
}

/**
 * The current quarter start plus `count - 1` preceding quarters,
 * newest first — drives the quarter picker, like `rollingMondays`.
 */
export function rollingQuarters(now: Date = new Date(), count = 4): Date[] {
  const start = quarterStartOf(now);
  const out: Date[] = [];
  for (let i = 0; i < count; i++) {
    const monthsBack = start.getUTCMonth() - i * 3;
    out.push(new Date(Date.UTC(start.getUTCFullYear(), monthsBack, 1)));
  }
  return out;
}

/** "Q3 2026" display label for a quarter-start date. */
export function quarterLabel(quarterStart: Date): string {
  const q = quarterStart.getUTCMonth() / 3 + 1;
  return `Q${String(q)} ${String(quarterStart.getUTCFullYear())}`;
}

/** First day (UTC midnight) of the quarter AFTER the one containing `date`. */
export function nextQuarterStart(date: Date): Date {
  const start = quarterStartOf(date);
  return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 3, 1));
}

/** Whole days from `now` until `target` (floored); negative once passed. */
export function daysUntil(target: Date, now: Date): number {
  return Math.floor((target.getTime() - now.getTime()) / 86_400_000);
}

export function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}
